import { isEqual } from 'lodash'
import { Reducer } from 'redux'
import { IDatLane } from '../contractLanes/types'
import { IMarketplaceStore } from '../types'
import { LANE_METRICS_ACTION_TYPES as c } from './actions'
import { ILaneMetric, ILaneMetricsState, ILaneMetricsParams } from './types'

const initState = {
  data: undefined,
  loading: false,
  error: '',
  totalCount: 0,
  currentFilters: {},
  params: {
    pageCount: 0,
    perPage: 20,
  },
}

interface ILaneMetricsResponsePayload {
  laneMetrics: ILaneMetric[]
  totalCount: number
  currentFilters: {}
  requestData: { params: ILaneMetricsParams }
}

interface ILaneMetricsRequest {
  type: c.FETCH_LANE_METRICS_REQUEST
  payload: ILaneMetricsResponsePayload
}

interface ILaneMetricsSuccess {
  type: c.FETCH_LANE_METRICS_SUCCESS
  payload: ILaneMetricsResponsePayload
}

interface ILaneMetricsFailure {
  type: c.FETCH_LANE_METRICS_FAILURE
  error: string
  payload: ILaneMetricsResponsePayload
}

interface ILaneMetricsClear {
  type: c.FETCH_LANE_METRICS_CLEAR
}

type LaneMetricsActions =
  | ILaneMetricsRequest
  | ILaneMetricsSuccess
  | ILaneMetricsFailure
  | ILaneMetricsClear

/** Reducer */
const laneMetricsReducer: Reducer<ILaneMetricsState, LaneMetricsActions> = (
  state: ILaneMetricsState = initState,
  action: LaneMetricsActions
): ILaneMetricsState => {
  switch (action.type) {
    case c.FETCH_LANE_METRICS_CLEAR: {
      return {
        ...state,
        loading: false,
        data: undefined,
        totalCount: 0,
      }
    }
    case c.FETCH_LANE_METRICS_REQUEST: {
      const {
        payload: {
          requestData: { params },
        },
      } = action as ILaneMetricsRequest
      return {
        loading: true,
        data: undefined,
        totalCount: 0,
        error: '',
        currentFilters: params,
      }
    }
    case c.FETCH_LANE_METRICS_SUCCESS: {
      const {
        payload: {
          requestData: { params },
        },
      } = action as ILaneMetricsSuccess
      if (isEqual(params, state.currentFilters)) {
        return {
          ...state,
          loading: false,
          data: action.payload.laneMetrics,
          totalCount: action.payload.totalCount,
        }
      }
      return state
    }
    case c.FETCH_LANE_METRICS_FAILURE: {
      const {
        error,
        payload: {
          requestData: { params },
        },
      } = action as ILaneMetricsFailure
      if (isEqual(params, state.currentFilters)) {
        return {
          ...state,
          loading: false,
          error,
          data: undefined,
          totalCount: 0,
        }
      }
      return state
    }
    default:
      return state
  }
}

export default laneMetricsReducer

/** Selectors */
export const selectMarketToMarketLanes = (state: IMarketplaceStore): ILaneMetric[] =>
  state.laneMetrics.data || []

export const selectMarketToMarketRequestData = (
  state: IMarketplaceStore
): { loading: boolean; error: string } => {
  return {
    loading: state.laneMetrics.loading,
    error: state.laneMetrics.error,
  }
}

export const selectLanesCount = (state: IMarketplaceStore): number =>
  state.laneMetrics.totalCount || 0

export const selectLaneInfo = (state: IMarketplaceStore): IDatLane | undefined => {
  if (state.laneMetrics?.data) {
    const {
      deliveryCityState,
      deliveryMarketId,
      pickupCityState,
      pickupMarketId,
    } = state.laneMetrics?.data[0].transfixLane
    const [pickupCity = '', pickupState = ''] = pickupCityState.split(',') || []
    const [deliveryCity = '', deliveryState = ''] = deliveryCityState.split(',') || []
    return {
      id: `${pickupMarketId}${deliveryMarketId}`,
      lane: `${pickupCityState} ${deliveryCityState}`,
      origin: pickupCityState,
      destination: deliveryCityState,
      originReferenceCity: pickupCity,
      originReferenceState: pickupState.trim(),
      destinationReferenceCity: deliveryCity,
      destinationReferenceState: deliveryState.trim(),
    }
  }
  return undefined
}
